import type { VariablesOf } from "@graphql-typed-document-node/core"
import { zodResolver } from "@hookform/resolvers/zod"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "@tanstack/react-router"
import { Plus } from "lucide-react"
import React, { useEffect, useState } from "react"
import { Controller, useForm } from "react-hook-form"
import { z } from "zod"

import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  toast,
} from "@atlant/ui"

import {
  FormAircraftCategorySelect,
  FormAirportSelect,
} from "#components/form-query/index.ts"
import { FormCountrySelect, FormInput, FormSelect } from "#components/form/index.ts"
import { graphql } from "#gql"
import type { AircraftSource } from "#gql/graphql.ts"
import { queryKeys } from "#lib/query-keys.ts"
import { useLoggedInUserInfo } from "#store/user-info.ts"
import { client } from "#utils/graphql-client.ts"

const AircraftCreateDocument = graphql(/* GraphQL */ `
  mutation AircraftCreate($input: MutationAircraftCreateInput!) {
    aircraftCreate(input: $input) {
      id
    }
  }
`)

const AircraftUpdateDocument = graphql(/* GraphQL */ `
  mutation AircraftUpdate($input: MutationAircraftUpdateInput!) {
    aircraftUpdate(input: $input) {
      id
      tail
      manufacturer
      model
      serial
      yom
      yor
      category
      pax
    }
  }
`)

const currentYear = new Date().getFullYear()

const yearOptions = Array.from({ length: currentYear - 1949 }, (_, i) => {
  const year = String(currentYear - i)
  return { value: year, label: year }
})

const generalInfoSchema = z.object({
  source: z.custom<AircraftSource>().optional(),
  tail: z.string().trim().min(1, { message: "Required" }),
  manufacturer: z.string().trim().min(1, { message: "Required" }),
  model: z.string().trim().min(1, { message: "Required" }),
  serial: z.string().trim().min(1, { message: "Required" }),
  yom: z.string().min(1, { message: "Required" }),
  yor: z.string().min(1, { message: "Required" }),
  category: z.string().min(1, { message: "Required" }),
  pax: z.string().min(1, { message: "Required" }),
  country: z.string().min(1, { message: "Required" }),
  locationId: z.string().optional(),
})

type GeneralInfoFormType = z.infer<typeof generalInfoSchema>

interface GeneralInfoDefaultValues {
  source: AircraftSource
  tail: string
  manufacturer: string
  model: string
  serial: string
  yom: string
  yor: string
  category: string
  pax: string
  country: string
  locationId: string
  locationLabel?: string
}

const emptyDefaults: GeneralInfoDefaultValues = {
  source: "MANUAL",
  tail: "",
  manufacturer: "",
  model: "",
  serial: "",
  yom: "",
  yor: "",
  category: "",
  pax: "",
  country: "",
  locationId: "",
  locationLabel: "",
}

interface AircraftGeneralInfoModalProps {
  aircraftId?: string
  defaultValues?: Partial<GeneralInfoDefaultValues>
  trigger?: React.ReactNode
  onSuccess?: () => void
}

export const AircraftGeneralInfoModal = ({
  aircraftId,
  defaultValues,
  trigger,
  onSuccess,
}: AircraftGeneralInfoModalProps) => {
  const isEdit = !!aircraftId
  const [open, setOpen] = useState(false)
  const queryClient = useQueryClient()
  const { me } = useLoggedInUserInfo()
  const navigate = useNavigate()

  const form = useForm<GeneralInfoFormType>({
    resolver: zodResolver(generalInfoSchema),
    defaultValues: { ...emptyDefaults, ...defaultValues },
    reValidateMode: "onChange",
  })
  const { reset } = form
  useEffect(() => {
    if (open && defaultValues) {
      reset({ ...emptyDefaults, ...defaultValues })
    }
  }, [open, reset, defaultValues])

  const { mutateAsync: createAircraft, isPending: isCreating } = useMutation({
    mutationFn: (variables: VariablesOf<typeof AircraftCreateDocument>) =>
      client.request(AircraftCreateDocument, variables),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.aircraft.all() })
      form.reset()
      setOpen(false)
      void navigate({
        to: "/$slug/fleet/$aircraftId/overview",
        params: { slug: me.organization.slug, aircraftId: data.aircraftCreate.id },
      })
    },
  })

  const { mutateAsync: updateAircraft, isPending: isUpdating } = useMutation({
    mutationFn: (variables: VariablesOf<typeof AircraftUpdateDocument>) =>
      client.request(AircraftUpdateDocument, variables),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.aircraft.detail(aircraftId!),
      })
      await queryClient.invalidateQueries({
        queryKey: queryKeys.aircraft.generalInfo(aircraftId!),
      })
      form.reset()
      setOpen(false)
      onSuccess?.()
    },
  })

  const isPending = isCreating || isUpdating

  const handleSubmit = form.handleSubmit((values) => {
    const promise = isEdit
      ? updateAircraft({
          input: {
            source: values.source || "LEON",
            id: aircraftId,
            organizationId: me.organization.id,
            tail: values.tail,
            manufacturer: values.manufacturer,
            model: values.model,
            serial: values.serial,
            yom: values.yom,
            yor: values.yor,
            category: values.category,
            pax: values.pax,
            country: values.country,
            locationId: values.locationId || "",
          },
        })
      : createAircraft({
          input: {
            tail: values.tail,
            manufacturer: values.manufacturer,
            model: values.model,
            serial: values.serial,
            yom: values.yom,
            yor: values.yor,
            category: values.category,
            pax: values.pax,
            country: values.country,
            locationId: values.locationId || "",
            organizationId: me.organization.id,
            files: [],
          },
        })

    toast.promise(promise as Promise<unknown>, {
      loading: isEdit ? "Updating aircraft..." : "Creating aircraft...",
      success: isEdit ? "Aircraft updated!" : "Aircraft created!",
    })
  })

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger ?? (
          <Button className="h-10 px-4">
            <Plus className="size-5" />
            Add first aircraft
          </Button>
        )}
      </DialogTrigger>

      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Edit General Information" : "Add General Information"}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Update core aircraft identity and registry data"
              : "Add core aircraft identity and registry data"}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Controller
              name="tail"
              control={form.control}
              render={({ field, fieldState }) => (
                <FormInput
                  {...field}
                  required
                  label="Registration"
                  placeholder="Add registration"
                  error={fieldState.error?.message}
                />
              )}
            />
            <Controller
              name="manufacturer"
              control={form.control}
              render={({ field, fieldState }) => (
                <FormInput
                  {...field}
                  required
                  label="Manufacturer"
                  placeholder="Add manufacturer"
                  error={fieldState.error?.message}
                />
              )}
            />
            <Controller
              name="model"
              control={form.control}
              render={({ field, fieldState }) => (
                <FormInput
                  required
                  {...field}
                  label="Model"
                  placeholder="Add model"
                  error={fieldState.error?.message}
                />
              )}
            />
            <Controller
              name="serial"
              control={form.control}
              render={({ field, fieldState }) => (
                <FormInput
                  {...field}
                  required
                  onChange={(e) => field.onChange(e.toLocaleUpperCase())}
                  label="Serial number"
                  placeholder="Add serial number"
                  error={fieldState.error?.message}
                />
              )}
            />
            <Controller
              name="yom"
              control={form.control}
              render={({ field, fieldState }) => (
                <FormSelect
                  value={field.value}
                  onChange={field.onChange}
                  name={field.name}
                  label="Year of manufacture"
                  placeholder="Add year of manufacture"
                  options={yearOptions}
                  error={fieldState.error?.message}
                />
              )}
            />
            <Controller
              name="yor"
              control={form.control}
              render={({ field, fieldState }) => (
                <FormSelect
                  value={field.value}
                  onChange={field.onChange}
                  name={field.name}
                  label="Year of refurbishment"
                  placeholder="Add year of refurbishment"
                  options={yearOptions}
                  error={fieldState.error?.message}
                />
              )}
            />
            <Controller
              name="category"
              control={form.control}
              render={({ field, fieldState }) => (
                <FormAircraftCategorySelect
                  value={field.value}
                  onChange={field.onChange}
                  name={field.name}
                  error={fieldState.error?.message}
                />
              )}
            />
            <Controller
              name="pax"
              control={form.control}
              render={({ field, fieldState }) => (
                <FormInput
                  {...field}
                  type="number"
                  onChange={(val) => {
                    const parsed = val === "" ? "" : Number(val)
                    field.onChange(String(parsed))
                  }}
                  label="Passenger capacity"
                  placeholder="Add passenger capacity"
                  error={fieldState.error?.message}
                />
              )}
            />
            <Controller
              name="country"
              control={form.control}
              render={({ field, fieldState }) => (
                <FormCountrySelect
                  value={field.value}
                  onChange={field.onChange}
                  name={field.name}
                  label="Country of registry"
                  placeholder="Add country of registry"
                  error={fieldState.error?.message}
                />
              )}
            />
            <Controller
              name="locationId"
              control={form.control}
              render={({ field, fieldState }) => (
                <FormAirportSelect
                  sources={
                    defaultValues?.source
                      ? [defaultValues.source]
                      : ["MANUAL", "PLATFORM"]
                  }
                  value={field.value ?? ""}
                  onChange={field.onChange}
                  name={field.name}
                  label="Home base"
                  placeholder="Add home base"
                  initialLabel={defaultValues?.locationLabel}
                  error={fieldState.error?.message}
                />
              )}
            />
          </div>

          <DialogFooter className="gap-2 border-none! bg-inherit">
            <DialogClose className="w-1/2" asChild>
              <Button
                type="button"
                variant="outline"
                className="h-10 flex-1 sm:flex-none"
              >
                Cancel
              </Button>
            </DialogClose>
            <Button
              type="submit"
              disabled={isPending}
              className="h-10 w-1/2 flex-1 sm:flex-none"
            >
              {isEdit ? "Update" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
